// iter146 — Shared mobile sticky action bar. Portals to <body> to escape
// the `.page-transition` wrapper's containing block (which used to re-parent
// `position: fixed` descendants into itself whenever a `transform` or
// `will-change: transform` was set, dropping sticky buttons several thousand
// pixels off-screen — see iter69/iter142 RCAs).
//
// Props:
//   - testId:   data-testid for the <button> (required so tests can poke it).
//   - onClick:  tap handler.
//   - disabled: boolean; dims and blocks the button.
//   - children: button content (icon + label).
//   - secondary (optional): small line of text rendered below the button
//                           (e.g. "Last updated: 10:12 AM").
//   - variant (optional): "primary" (default, clay / indigo) | "ink" (slate).
//
// Positioning: fixed, inset-x-0, md:hidden, z-50, bottom =
// `72px + env(safe-area-inset-bottom, 0px)`. BottomNav is z-40 ~60-64px tall,
// so 72 clears it with a visible gap. Scale on press + 150ms transition.
import { createPortal } from "react-dom";

export default function StickyActionBar({
  testId,
  onClick,
  disabled = false,
  children,
  secondary = null,
  secondaryTestId,
  variant = "primary",
  type = "button",
}) {
  if (typeof document === "undefined") return null;
  const base =
    "flex w-full items-center justify-center gap-2.5 rounded-2xl px-5 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-all active:scale-[0.99] disabled:opacity-50";
  // iter147 — Shadow colour follows the active --clay/--ink CSS var so the
  // halo re-tints automatically when the palette switches (indigo in-app,
  // terracotta in .report-scope).
  const variantCls =
    variant === "ink"
      ? "bg-ink-900 shadow-[0_12px_28px_-12px_rgb(var(--ink-900)/0.7)]"
      : "bg-clay shadow-[0_12px_28px_-12px_rgb(var(--clay)/0.7)]";
  return createPortal(
    <div
      className="fixed inset-x-0 z-50 px-4 pt-3 md:hidden"
      style={{ bottom: "calc(72px + env(safe-area-inset-bottom, 0px))" }}
      data-testid="sticky-action-bar"
    >
      <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
        className={`${base} ${variantCls}`}
      >
        {children}
      </button>
      {secondary && (
        <p
          className="mt-2 text-center text-[0.65rem] font-medium text-ink-700"
          data-testid={secondaryTestId}
        >
          {secondary}
        </p>
      )}
    </div>,
    document.body,
  );
}
